import { useTranslation } from "react-i18next";
import { FileUploader } from "react-drag-drop-files";
import { CheckCircle } from "lucide-react";
import CONFIG from "../../config/config";
import { useState } from "react";
import { StyleSheetManager } from "styled-components";

/** Restores the dropper chrome. Custom children clear the library's default box styles. */
const SUCCESS_DROPPER_CLASSES =
  "flex h-12 w-full min-w-[322px] max-w-[508px] cursor-pointer items-center rounded-[5px] border-2 border-dashed border-[#0658c2] py-2 pl-2 pr-4";

/** Values for `{{name}}` placeholders in i18n strings. */
type I18nInterpolation = Record<string, string | number>;

type Props = {
  label?: string,
  /** Interpolation values for `label` (e.g. `{ maxSize: 2.5 }` when the label contains `{{maxSize}}`). */
  labelOptions?: I18nInterpolation,
  onChange: (input: any) => void,
  isDisabled?: boolean,
  allowedTypes?: string[],
  allowedMultipleFiles?: boolean,
  maxSize?: number,
  /** When set, the size error message includes {{maxSize}} (MB) via `errors.too_heavy_max_mb`. */
  showMaxSizeInSizeError?: boolean,
  /** When true and a file is already chosen, show a green check beside the success text. */
  showSuccessCheck?: boolean,
  hasSelection?: boolean,
};

function FileUploadWrapper({ label = '', labelOptions, isDisabled = false, onChange = () => null, allowedTypes = CONFIG.FILE_TYPES, allowedMultipleFiles = false, maxSize = CONFIG.MAX_FILE_UPLOAD, showMaxSizeInSizeError = false, showSuccessCheck = false, hasSelection = false }: Props) {

  const [error, setError] = useState<{ key: string; options?: I18nInterpolation } | undefined>();
  const { t } = useTranslation();
  
  const wrapOnChange = (params: any) => {
    setError(undefined);
    onChange(params);
  };

  const labelText =
    labelOptions != null
      ? (t(label, labelOptions) as string)
      : (t(label) as string);

  const successContent = showSuccessCheck && hasSelection ? (
    <div className="flex w-full min-w-0 items-center justify-between gap-3">
      <span className="inline-flex min-w-0 items-center gap-2 text-sm text-[#666]">
        <CheckCircle
          className="h-5 w-5 shrink-0 text-green-600"
          data-testid="file-upload-success-check"
          aria-hidden
        />
        <span className="underline">{t("file_upload.uploaded_successfully")}</span>
      </span>
      <span className="file-types shrink-0 text-xs text-[#666]">{allowedTypes.join(",")}</span>
    </div>
  ) : undefined;

  return (
    <div className="w-full max-w-full min-w-0">
      { error && (
        <p className="text-xs text-red-600 my-0">
          {(error.options != null
            ? t(error.key, error.options)
            : t(error.key)) as string}
        </p>
      )}
      <div className="w-full max-w-full min-w-0">
        <StyleSheetManager shouldForwardProp={(propName) => propName !== "error"}>
          <FileUploader 
            label={labelText} 
            handleChange={wrapOnChange} 
            name="file" 
            types={allowedTypes}
            maxSize={maxSize}
            disabled={isDisabled}
            multiple={allowedMultipleFiles}
            classes={successContent ? SUCCESS_DROPPER_CLASSES : undefined}
            onSizeError={() => {
              console.error("File size error occurred.");
              setError(
                showMaxSizeInSizeError && maxSize != null
                  ? { key: "errors.too_heavy_max_mb", options: { maxSize } }
                  : { key: "errors.too_heavy" }
              );
            }}
          >
            {successContent}
          </FileUploader>
        </StyleSheetManager>
      </div>
    </div>
  );
}

export default FileUploadWrapper;
